import { searchJmesPath } from '../../../utils/jmespath';
import { evaluateCondition } from './condition';
import type { TemplateNode } from './types';

function padTwo(n: number): string {
  return String(n).padStart(2, '0');
}

function formatDateValue(value: unknown, format: string): string {
  if (value == null || value === '') return '';
  const date = new Date(String(value));
  if (isNaN(date.getTime())) return String(value);

  return format
    .replace('yyyy', String(date.getFullYear()))
    .replace('MM', padTwo(date.getMonth() + 1))
    .replace('dd', padTwo(date.getDate()))
    .replace('HH', padTwo(date.getHours()))
    .replace('mm', padTwo(date.getMinutes()))
    .replace('ss', padTwo(date.getSeconds()));
}

function applyFunction(name: string, value: unknown, format?: string): string {
  if (value == null) return '';
  const str = String(value);

  switch (name) {
    case 'uppercase':
      return str.toUpperCase();
    case 'lowercase':
      return str.toLowerCase();
    case 'formatDate':
    case 'formatDateTime':
      return formatDateValue(value, format ?? 'dd.MM.yyyy');
    default:
      return str;
  }
}

export function evaluate(nodes: TemplateNode[], context: object): string {
  let output = '';

  for (const node of nodes) {
    if (node.type === 'text') {
      output += node.html;
    } else if (node.type === 'variable') {
      const value = searchJmesPath(context, node.path);
      if (node.persist && value == null) {
        output += node.originalHtml ?? '';
      } else {
        output += `<span>${value ?? ''}</span>`;
      }
    } else if (node.type === 'function') {
      const value = searchJmesPath(context, node.variablePath);
      output += `<span>${applyFunction(node.functionName, value, node.format)}</span>`;
    } else if (node.type === 'if') {
      if (evaluateCondition(node.expression, context)) {
        output += evaluate(node.children, context);
      }
    } else if (node.type === 'loop') {
      const collection = searchJmesPath(context, node.collectionPath);
      if (Array.isArray(collection)) {
        for (const [index, item] of collection.entries()) {
          const isObject = typeof item === 'object' && item !== null;
          let childContext = {};

          if (isObject) {
            childContext = {
              ...context,
              [node.itemName]: { ...item, index: index + 1 },
            };
          } else {
            childContext = {
              ...context,
              [node.itemName]: { value: item, index: index + 1 },
            };
          }

          output += evaluate(node.children, childContext);
        }
      }
    }
  }

  return output;
}
